import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, ClosableDrawer } from '@atoms/layout';
import { Chip, InputAdornment, Stack, styled, Tooltip, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { useForm } from 'react-hook-form';
import { FormAutocompleteMultiple, FormBlock, FormPhoneNumber, FormSelect, FormTextField, Form } from '@molecules/form';
import { useNotification } from '@libs/snackbar';
import { AgencyUpdateData } from '@libs/models/agencies';
import { ContactInfo } from '@libs/models/common/contactInfo';
import { Place } from '@libs/models/common';
import { useAgencyEditStore } from '@libs/store/agencies/agencyEditDrawerStore';
import { emailPattern } from '@libs/forms';
import { useLinesOfBusinessListStore } from '@libs/store/linesOfBusiness';
import { useAgencyTypesListStore } from '@libs/store/agencyTypes';
import { AddressAutocomplete, Place as AutocompletePlace } from '@atoms/inputs';
import { useQualificationsListStore } from '@libs/store/qualifications/listStore';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

type EditShiftDrawerProps = {
  onSaveCall?: () => void;
  localizationSubset: string;
};

export function EditAgencyDrawer({ onSaveCall, localizationSubset }: EditShiftDrawerProps) {
  const intl = useIntl();
  const { showError } = useNotification();

  const {
    drawerOpen,
    drawerMode,
    closeDrawer,
    addAgency,
    updateAgency,
    currentEntityId: currentId,
  } = useAgencyEditStore();

  const {
    linesOfBusiness: linesOfBusinessList,
    loadingList: linesOfBusinessListLoading,
    load: loadLinesOfBusiness,
  } = useLinesOfBusinessListStore();

  const {
    agencyTypes: agencyTypesList,
    loadingList: agencyTypesListLoading,
    load: loadAgencyTypes,
  } = useAgencyTypesListStore();

  const {
    qualifications: qualificationsList,
    loadingList: qualificationsListLoading,
    load: loadQualifications,
  } = useQualificationsListStore();

  const isAddDialog = drawerMode == 'add';
  localizationSubset = isAddDialog ? `${localizationSubset}.add` : `${localizationSubset}.edit`;
  const open = drawerOpen;

  const loading = linesOfBusinessListLoading || agencyTypesListLoading || qualificationsListLoading;

  useEffect(() => {
    if (!linesOfBusinessList.length) {
      loadLinesOfBusiness();
    }
  }, [loadLinesOfBusiness, linesOfBusinessList]);

  useEffect(() => {
    if (!agencyTypesList.length) {
      loadAgencyTypes();
    }
  }, [loadAgencyTypes, agencyTypesList]);

  useEffect(() => {
    if (!qualificationsList.length) {
      loadQualifications();
    }
  }, [loadQualifications, qualificationsList]);

  const methods = useForm<AgencyFormData>({
    defaultValues: {
      name: '',
      domainName: '',
      agencyType: '',
      linesOfBusiness: [],
      qualifications: [],
      contactFirstName: '',
      contactLastName: '',
      contactPhoneNumber: '',
      addressAddressFirst: '',
      addressAddressSecond: '',
      addressCity: '',
      addressState: '',
      addressZipCode: '',
    },
  });
  const {
    setValue,
    setError,
    watch,
    formState: { errors },
  } = methods;

  const handleClose = () => {
    closeDrawer();
    methods.reset();
  };
  const handleSubmit = async (isPublish: boolean, data: AgencyFormData) => {
    const sendingData: AgencyUpdateData = {
      id: '',
      name: data.name,
      domainName: data.domainName,
      agencyType: data.agencyType,
      linesOfBusiness: data.linesOfBusiness,
      qualifications: data.qualifications,
      contact: {
        email: data.contactEmail,
        firstName: data.contactFirstName,
        phoneNumber: data.contactPhoneNumber,
        lastName: data.contactLastName,
      },
      address: {
        addressFirst: data.addressAddressFirst,
        addressSecond: data.addressAddressSecond,
        city: data.addressCity,
        state: data.addressState,
        zipCode: data.addressZipCode,
      },
    };

    try {
      if (isAddDialog) {
        await addAgency(sendingData);
      } else {
        await updateAgency(currentId, sendingData);
      }

      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      }
    }
  };

  const handleAddActivate = async () => {};
  const handleAdd = async () => {};

  const linesOfBusiness = watch('linesOfBusiness');
  const listOfQualifications = watch('qualifications');
  const address1 = watch('addressAddressFirst');
  const agencyName = watch('name');
  useEffect(() => {
    if (agencyName) {
      setValue('domainName', agencyName.replace(/\s+/g, '-').toLowerCase());
    }
  }, [agencyName, setValue]);

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      onClose={handleClose}
      controls={
        <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
          <MenuButton variant="text" onClick={handleAddActivate} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.addActivate.label`} />
          </MenuButton>
          <MenuButton variant="text" color={'error'} onClick={handleAdd} disabled={loading}>
            <FormattedMessage id={`${localizationSubset}.menuButtons.add.label`} />
          </MenuButton>
          <Box flex={1} />
          <MenuButton
            onClick={handleClose}
            sx={(theme) => ({ color: theme.palette.text.secondary })}
            disabled={loading}
          >
            <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
          </MenuButton>
        </Stack>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
          </Typography>
          <Box flex={1} />
        </Stack>
        <Form formUtils={methods} style={{ height: '100%' }} onSubmit={handleSubmit.bind(handleSubmit, false)}>
          {/* Basics */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.details.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled={loading}
                label={<FormattedMessage id={`${localizationSubset}.details.agencyName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.agencyName.placeholder`,
                })}
                required
                fullWidth
                name="name"
              />
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.details.domainName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.domainName.placeholder`,
                })}
                fullWidth
                name="domainName"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FormattedMessage id={`${localizationSubset}.details.domainName.endAdornment`} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormSelect
                disabled={loading}
                options={agencyTypesList.map((type) => ({
                  value: type.id,
                  label: type.name,
                  key: type.id,
                }))}
                label={<FormattedMessage id={`${localizationSubset}.details.agencyType.label`} />}
                fullWidth
                required
                name="agencyType"
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.details.agencyType.placeholder`,
                })}
              />
              <FormAutocompleteMultiple
                options={linesOfBusinessList.map((lob) => ({
                  value: lob.id,
                  label: lob.name,
                  key: lob.id,
                }))}
                disabled={loading}
                required
                fullWidth
                label={<FormattedMessage id={`${localizationSubset}.details.linesOfBusiness.label`} />}
                textFieldProps={{
                  placeholder:
                    linesOfBusiness.length === 0
                      ? intl.formatMessage({
                          id: `${localizationSubset}.details.linesOfBusiness.placeholder`,
                        })
                      : undefined,
                }}
                disableCloseOnSelect
                name="linesOfBusiness"
                requiredMessage={intl.formatMessage({
                  id: `${localizationSubset}.details.linesOfBusiness.requiredMessage`,
                })}
              />
            </Stack>
          </FormBlock>
          {/* Contact */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.contact.sectTitle`} />}>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.firstName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.firstName.placeholder`,
                })}
                fullWidth
                name="firstName"
              />
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.lastName.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.lastName.placeholder`,
                })}
                fullWidth
                name="lastName"
              />
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <FormTextField
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.email.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.email.placeholder`,
                })}
                fullWidth
                name="email"
                options={{
                  maxLength: 254,
                  pattern: {
                    value: emailPattern,
                    message: intl.formatMessage({
                      id: 'forms.invalidEmail',
                    }),
                  },
                }}
              />
              <FormPhoneNumber
                disabled={loading}
                required
                label={<FormattedMessage id={`${localizationSubset}.contact.phoneNumber.label`} />}
                placeholder={intl.formatMessage({
                  id: `${localizationSubset}.contact.phoneNumber.placeholder`,
                })}
                fullWidth
                name="phoneNumber"
              />
            </Stack>
          </FormBlock>

          {/* Address */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.address.sectTitle`} />}>
            <Stack direction={'column'} spacing={1}>
              <Stack direction={'row'} spacing={2}>
                <AddressAutocomplete
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressAddressFirst.label`} />}
                  fullWidth
                  required
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressAddressFirst.placeholder`,
                  })}
                  noOptionsText={intl.formatMessage({
                    id: `${localizationSubset}.address.addressAddressFirst.noLocations`,
                  })}
                  {...methods.register('addressAddressFirst', {
                    required: intl.formatMessage({
                      id: 'forms.fieldIsRequired',
                    }),
                  })}
                  onChange={(newValue: AutocompletePlace | null) => {
                    setValue('addressAddressFirst', newValue?.description ?? undefined, {
                      shouldValidate: true,
                    });
                    if (!newValue) {
                      // When clearing the address, leave the city, state and zip
                      return;
                    }
                    setValue('addressCity', newValue?.city, {
                      shouldValidate: true,
                    });
                    setValue('addressState', newValue?.state, {
                      shouldValidate: true,
                    });
                    setValue('addressZipCode', newValue?.zip ?? '', {
                      shouldValidate: true,
                    });
                  }}
                  value={address1}
                  error={errors.addressAddressFirst?.message}
                />
              </Stack>
              <Stack direction={'row'} spacing={2}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressAddressSecond.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressAddressSecond.placeholder`,
                  })}
                  fullWidth
                  name="addressAddressSecond"
                />
              </Stack>
              <Stack direction={'row'} spacing={2}>
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressCity.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressCity.placeholder`,
                  })}
                  fullWidth
                  name="addressCity"
                />
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressState.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressState.placeholder`,
                  })}
                  fullWidth
                  name="addressState"
                />
                <FormTextField
                  disabled={loading}
                  label={<FormattedMessage id={`${localizationSubset}.address.addressZipCode.label`} />}
                  placeholder={intl.formatMessage({
                    id: `${localizationSubset}.address.addressZipCode.placeholder`,
                  })}
                  fullWidth
                  name="addressZipCode"
                  options={{
                    pattern: {
                      value: /^\d{5}(?:[-\s]\d{4})?$/,
                      message: intl.formatMessage({
                        id: 'forms.invalidZipCode',
                      }),
                    },
                  }}
                />
              </Stack>
            </Stack>
          </FormBlock>
          {/* Qualifications */}
          <FormBlock title={<FormattedMessage id={`${localizationSubset}.qualifications.sectTitle`} />}>
            <Stack direction={'column'} spacing={1}>
              <Stack direction={'row'} spacing={2}>
                <FormAutocompleteMultiple
                  fullWidth
                  disabled={loading}
                  options={qualificationsList.map((qual) => ({
                    value: qual.id,
                    label: qual.abbreviation,
                    key: qual.id,
                  }))}
                  label={<FormattedMessage id={`${localizationSubset}.qualifications.qualifications.label`} />}
                  textFieldProps={{
                    placeholder:
                      listOfQualifications.length === 0
                        ? intl.formatMessage({
                            id: `${localizationSubset}.qualifications.qualifications.placeholder`,
                          })
                        : undefined,
                  }}
                  renderTags={(selected, getTagProps) =>
                    selected.map((s, i) => {
                      const tagProps = getTagProps({ index: i });
                      return (
                        <Chip
                          label={s.label}
                          {...tagProps}
                          deleteIcon={<DeleteQualificationIcon />}
                          onDelete={(e) => tagProps.onDelete(e)}
                          key={s.value}
                        />
                      );
                    })
                  }
                  disableCloseOnSelect
                  name={'qualifications'}
                  limitTags={6}
                />
              </Stack>
            </Stack>
          </FormBlock>
        </Form>
      </Stack>
    </ClosableDrawer>
  );
}
const MenuButton = styled(Button)(({ theme }) => ({
  // minWidth: theme.spacing(16.75),
}));

type AgencyFormData = {
  domainName: AgencyUpdateData['domainName'];
  qualifications: AgencyUpdateData['qualifications'];
  name: AgencyUpdateData['name'];
  agencyType: AgencyUpdateData['agencyType'];
  linesOfBusiness: AgencyUpdateData['linesOfBusiness'];
  contactFirstName: ContactInfo['firstName'];
  contactLastName: ContactInfo['lastName'];
  contactPhoneNumber: ContactInfo['phoneNumber'];
  contactEmail: ContactInfo['email'];
  addressAddressFirst: Place['addressFirst'];
  addressAddressSecond: Place['addressSecond'];
  addressCity: Place['city'];
  addressState: Place['state'];
  addressZipCode: Place['zipCode'];
};

type DeleteQualificationIconProps = React.ComponentProps<typeof CancelRoundedIcon> & {
  disabled?: boolean;
};
function DeleteQualificationIcon({ disabled, ...props }: DeleteQualificationIconProps) {
  return disabled ? (
    <Tooltip
      title={<FormattedMessage id="shifts.editShiftDrawer.disabledQualificationTooltip" />}
      arrow
      placement="right"
    >
      <CancelRoundedIcon {...props} />
    </Tooltip>
  ) : (
    <CancelRoundedIcon {...props} />
  );
}
